import React, { ChangeEvent, FormEvent, MutableRefObject, useEffect, useRef, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { AppState, fetchSearch, selectors, useAppDispatch, useAppSelector } from "../../state";
import { Badge, Card, CardBody, CardHeader, Col, Container, Form, Input, Label, Row } from "reactstrap";
import { Helmet } from "react-helmet";
import { ShowLoading } from "../handlers/ShowLoading";
import { LinkToContentSummaryList } from "../elements/list-groups/ContentSummaryListGroupItem";
import {
  DOCUMENT_TYPE,
  documentDescription,
  isIntendedAudience,
  parseLocationSearch,
  pushSearchToHistory,
  SEARCH_CHAR_LENGTH_LIMIT,
  searchResultIsPublic,
  selectOnChange,
  shortcuts,
  useUserContext,
} from "../../services";
import { TitleAndBreadcrumb } from "../elements/TitleAndBreadcrumb";
import { ShortcutResponse } from "../../../IsaacAppTypes";
import { UserContextPicker } from "../elements/inputs/UserContextPicker";
import Select, { CSSObjectWithLabel, GroupBase, StylesConfig } from "react-select";
import { IsaacSpinner } from "../handlers/IsaacSpinner";

interface Item<T> {
  value: T;
  label: string;
}

function itemise(document: DOCUMENT_TYPE): Item<DOCUMENT_TYPE> {
  return { value: document, label: documentDescription[document] };
}
function deitemise(item: Item<DOCUMENT_TYPE>) {
  return item.value;
}

const selectStyle: StylesConfig<Item<DOCUMENT_TYPE>, true, GroupBase<Item<DOCUMENT_TYPE>>> = {
  multiValue: (styles: CSSObjectWithLabel) => ({ ...styles, backgroundColor: "rgba(255, 181, 63, 0.9)" }),
  multiValueLabel: (styles: CSSObjectWithLabel) => ({ ...styles, color: "black" }),
};

// Interacting with the page's filters change the query parameters.
// Whenever the query parameters change we send a search request to the API.
export const Search = withRouter((props: RouteComponentProps) => {
  const { location, history } = props;
  const dispatch = useAppDispatch();
  const searchResults = useAppSelector((state: AppState) => state?.search?.searchResults || null);
  const user = useAppSelector(selectors.user.orNull);
  const userContext = useUserContext();
  const [urlQuery, urlFilters] = parseLocationSearch(location.search);
  const [queryState, setQueryState] = useState(urlQuery);

  let initialFilters = urlFilters;
  if (urlFilters.length === 0) {
    initialFilters = [DOCUMENT_TYPE.CONCEPT, DOCUMENT_TYPE.EVENT, DOCUMENT_TYPE.TOPIC_SUMMARY, DOCUMENT_TYPE.GENERIC];
  }
  const [filtersState, setFiltersState] = useState<Item<DOCUMENT_TYPE>[]>(initialFilters.map(itemise));

  useEffect(
    function triggerSearchAndUpdateLocalStateOnUrlChange() {
      dispatch(fetchSearch(urlQuery ?? "", initialFilters.length ? initialFilters.join(",") : undefined));
      setQueryState(urlQuery);
      setFiltersState(initialFilters.map(itemise));
    },
    [dispatch, location.search],
  );

  function updateSearchUrl(e?: FormEvent<HTMLFormElement>) {
    if (e) {
      e.preventDefault();
    }
    pushSearchToHistory(history, queryState || "", filtersState.map(deitemise));
  }

  // Trigger update to search url on query or filter change
  const timer: MutableRefObject<number | undefined> = useRef();
  useEffect(() => {
    if (queryState !== urlQuery) {
      timer.current = window.setTimeout(() => {
        updateSearchUrl();
      }, 800);
      return () => {
        clearTimeout(timer.current);
      };
    }
  }, [queryState]);

  useEffect(() => {
    const filtersStateMatchesQueryParamFilters =
      filtersState.length === initialFilters.length &&
      filtersState.map(deitemise).every((f) => initialFilters.includes(f));
    if (!filtersStateMatchesQueryParamFilters) {
      updateSearchUrl();
    }
  }, [filtersState]);

  // Process results and add shortcut responses
  const filteredSearchResults =
    searchResults?.results &&
    searchResults.results
      .filter((result) => searchResultIsPublic(result, user))
      .filter((result) => isIntendedAudience(result.audience, userContext, user));
  const shortcutResponses = (queryState ? shortcuts(queryState) : []) as ShortcutResponse[];
  const shortcutAndFilteredSearchResults = (shortcutResponses || []).concat(filteredSearchResults || []);

  return (
    <Container id="search-page">
      <Helmet>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Row>
        <Col>
          <TitleAndBreadcrumb currentPageTitle="Search" />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form inline onSubmit={updateSearchUrl}>
            <Input
              className="search--filter-input mt-4"
              type="search"
              value={queryState || ""}
              placeholder="Search"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setQueryState(e.target.value)}
              maxLength={SEARCH_CHAR_LENGTH_LIMIT}
            />
          </Form>
        </Col>
      </Row>
      <Row>
        <Col className="py-4">
          <Card>
            <CardHeader className="search-header">
              <Col md={5} sm={12}>
                <h3>
                  <span className="d-none d-sm-inline-block">Search&nbsp;</span>Results{" "}
                  {urlQuery != "" ? (
                    shortcutAndFilteredSearchResults ? (
                      <Badge color="primary">{shortcutAndFilteredSearchResults.length}</Badge>
                    ) : (
                      <IsaacSpinner />
                    )
                  ) : null}
                </h3>
              </Col>
              <Col md={7} sm={12}>
                <Form inline className="search-filters">
                  <Label htmlFor="document-filter" className="d-none d-lg-inline-block mr-1">
                    Filters
                  </Label>
                  <Select
                    inputId="document-filter"
                    isMulti
                    placeholder="No page type filter"
                    value={filtersState}
                    options={[
                      DOCUMENT_TYPE.CONCEPT,
                      DOCUMENT_TYPE.QUESTION,
                      DOCUMENT_TYPE.EVENT,
                      DOCUMENT_TYPE.TOPIC_SUMMARY,
                      DOCUMENT_TYPE.GENERIC,
                    ].map(itemise)}
                    className="basic-multi-select w-100 w-md-75 w-lg-50 mb-2 mb-md-0"
                    classNamePrefix="select"
                    onChange={selectOnChange(setFiltersState, false)}
                    styles={selectStyle}
                  />
                  <Label className="mt-2 mb-2 mb-md-0">
                    <UserContextPicker className="text-right" />
                  </Label>
                </Form>
              </Col>
            </CardHeader>
            {urlQuery != "" && (
              <CardBody>
                <ShowLoading until={shortcutAndFilteredSearchResults}>
                  {shortcutAndFilteredSearchResults && shortcutAndFilteredSearchResults.length > 0 ? (
                    <LinkToContentSummaryList items={shortcutAndFilteredSearchResults} displayTopicTitle={true} />
                  ) : (
                    <em>No results found</em>
                  )}
                </ShowLoading>
              </CardBody>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
});
